import type {
  CommunityKind,
  CommunityVisibility,
  FeedSort,
  MemberRole,
  Orientation,
  PostImageStatus,
  PostKind,
  PostTag,
} from '@mogzo/shared';
import { API_URL, ApiError, apiFetch } from './api';

export interface Author {
  name: string;
  /** Their role in the community today; null if they've left. */
  role: MemberRole | null;
  isYou: boolean;
}

/** A private community's page: a game's test community, or a team community (M28). */
export interface CommunityPageData {
  kind: 'test' | 'team';
  community: {
    id: string;
    name: string;
    slug: string;
    /** Where it lives: `/test/<address>` or `/team/<address>`. */
    path: string;
    /** The game and its developer, for test communities. */
    projectId: string | null;
    developerName: string | null;
    /** A team community's description. */
    about: string | null;
    createdAt: string;
    memberCount: number;
    members: { name: string; role: MemberRole }[];
  };
  viewer: { role: MemberRole; name: string };
  build: {
    id: string;
    number: number;
    version: string | null;
    createdAt: string;
    playUrl: string;
    /** The game's own splash page; null for MOGZO's default splash. */
    splashUrl: string | null;
    orientation: Orientation;
  } | null;
}

/** A build as the splash and the players need it. */
export interface PlayableBuild {
  number: number;
  playUrl: string;
  splashUrl: string | null;
  orientation: Orientation;
}

export interface PostImage {
  id: string;
  width: number;
  height: number;
  /** Signed links straight to storage (full size, and a small one for grids). */
  url: string;
  thumbUrl: string;
}

export interface Post {
  id: string;
  kind: PostKind;
  title: string | null;
  body: string;
  tag: PostTag | null;
  createdAt: string;
  pinned: boolean;
  ggCount: number;
  commentCount: number;
  viewerGg: boolean;
  author: Author;
  build: {
    number: number;
    version: string | null;
    playUrl: string | null;
    splashUrl: string | null;
    orientation: Orientation;
  } | null;
  images: PostImage[];
  developerReply: string | null;
  canRemove: boolean;
  canPin: boolean;
}

export interface Comment {
  id: string;
  parentId: string | null;
  depth: number;
  /** Null when removed (kept only because replies hang off it). */
  body: string | null;
  createdAt: string;
  ggCount: number;
  viewerGg: boolean;
  author: Author | null;
  canRemove: boolean;
}

/** A test community's page, with its game. */
export type TestCommunity = CommunityPageData & {
  kind: 'test';
  community: { projectId: string; developerName: string };
};

export interface PostThread {
  post: Post;
  community: {
    id: string;
    kind: CommunityKind;
    name: string;
    slug: string;
    path: string;
    projectId: string | null;
    developerName: string | null;
  };
  comments: Comment[];
}

export type InviteStatus = 'active' | 'expired' | 'used_up' | 'turned_off';

export interface Invite {
  status: InviteStatus;
  invitedBy: string | null;
  expiresAt: string;
  usesLeft: number;
  community: {
    kind: CommunityKind;
    name: string;
    slug: string;
    about: string | null;
    developerName: string | null;
    adultsOnly: boolean;
    memberCount: number;
    members: string[];
  };
  viewer: { name: string; isMember: boolean; requested: boolean; oldEnough: boolean } | null;
}

export interface JoinAnswer {
  /** `member`: in already. `requested`: waiting for a manager. */
  status: 'member' | 'requested';
  kind: CommunityKind;
  slug: string;
  path: string;
}

export interface InviteLink {
  id: string;
  url: string;
  uses: number;
  maxUses: number;
  expiresAt: string;
  status: InviteStatus;
}

export interface MembersTab {
  viewer: { role: MemberRole; canManage: boolean };
  members: {
    userId: string;
    name: string;
    username: string | null;
    role: MemberRole;
    joinedAt: string;
    isYou: boolean;
  }[];
  /** Managers only. */
  requests: { userId: string; name: string; username: string | null; requestedAt: string }[];
  invites: InviteLink[];
}

/** A community in search results and the library. */
export interface CommunitySummary {
  id: string;
  kind: CommunityKind;
  visibility: CommunityVisibility;
  name: string;
  slug: string;
  path: string;
  memberCount: number;
}

export interface LibraryCommunity extends CommunitySummary {
  role: MemberRole;
  /** People waiting to join; counted for managers only. */
  requestCount: number;
}

export interface SearchResults {
  games: { name: string; slug: string; path: string }[];
  communities: (CommunitySummary & { isMember: boolean })[];
}

const postJson = (body: object) => ({ method: 'POST', body: JSON.stringify(body) });

type TestCommunityAnswer = Omit<TestCommunity, 'kind' | 'community'> & {
  community: Omit<TestCommunity['community'], 'path' | 'about'>;
};

/** The community, with the newest build or (from a build's post) the one numbered `build`. */
export async function fetchTestCommunity(slug: string, build?: number): Promise<TestCommunity> {
  const data = await apiFetch<TestCommunityAnswer>(
    `/v1/communities/test/${encodeURIComponent(slug)}${build ? `?build=${build}` : ''}`,
  );
  return {
    ...data,
    kind: 'test',
    community: { ...data.community, path: `/test/${data.community.slug}`, about: null },
  };
}

/** A team community's page (M28). */
export async function fetchTeamCommunity(slug: string): Promise<CommunityPageData> {
  const data = await apiFetch<{
    community: Omit<CommunityPageData['community'], 'path' | 'projectId' | 'developerName'> & {
      about: string;
    };
    viewer: CommunityPageData['viewer'];
  }>(`/v1/communities/team/${encodeURIComponent(slug)}`);
  return {
    kind: 'team',
    community: {
      ...data.community,
      path: `/team/${data.community.slug}`,
      projectId: null,
      developerName: null,
    },
    viewer: data.viewer,
    build: null,
  };
}

export function fetchPosts(
  communityId: string,
  sort: FeedSort,
  offset = 0,
): Promise<{ posts: Post[]; hasMore: boolean }> {
  return apiFetch(`/v1/communities/${communityId}/posts?sort=${sort}&offset=${offset}`);
}

export function createPost(
  communityId: string,
  post: { body: string; tag: PostTag | null; imageIds: string[] },
): Promise<Post> {
  const { body, tag, imageIds } = post;
  return apiFetch(
    `/v1/communities/${communityId}/posts`,
    postJson({ body, tag: tag ?? undefined, imageIds: imageIds.length ? imageIds : undefined }),
  );
}

/** A picture being prepared: sizes and links arrive once it's ready. */
interface PictureStatus {
  id: string;
  status: PostImageStatus;
  error: string | null;
  width: number | null;
  height: number | null;
  url: string | null;
  thumbUrl: string | null;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
/** How long to wait for the worker to prepare a picture before giving up. */
const PICTURE_WAIT_MS = 60_000;

/**
 * Uploads one picture for a post being written, then waits while MOGZO prepares it (it's
 * checked, shrunk and re-encoded in the background, usually within a second or two).
 */
export async function uploadPostImage(communityId: string, file: File): Promise<PostImage> {
  const response = await fetch(`${API_URL}/v1/communities/${communityId}/images`, {
    method: 'POST',
    body: file,
    credentials: 'include',
    headers: { 'content-type': file.type },
    signal: AbortSignal.timeout(60_000),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? 'Couldn’t add that picture. Try again.',
    );
  }
  const id = (body as { id: string }).id;
  const giveUpAt = Date.now() + PICTURE_WAIT_MS;
  for (let delay = 400; Date.now() < giveUpAt; delay = Math.min(delay * 1.5, 2000)) {
    await wait(delay);
    const image = await apiFetch<PictureStatus>(`/v1/post-images/${id}`);
    if (image.status === 'failed') {
      throw new ApiError(422, 'bad_image', image.error ?? 'Couldn’t add that picture.');
    }
    if (image.status === 'ready' && image.url && image.thumbUrl) {
      return {
        id,
        width: image.width ?? 0,
        height: image.height ?? 0,
        url: image.url,
        thumbUrl: image.thumbUrl,
      };
    }
  }
  throw new ApiError(504, 'picture_slow', 'That picture is taking too long. Try again.');
}

/** Takes back a picture that hasn't been posted yet. */
export function removePendingImage(id: string) {
  return apiFetch<{ ok: true }>(`/v1/post-images/${id}/remove`, postJson({}));
}

export function fetchMembers(communityId: string): Promise<MembersTab> {
  return apiFetch(`/v1/communities/${communityId}/members`);
}

/** A manager lets someone in, or turns them away. */
export function answerRequest(communityId: string, userId: string, answer: 'accept' | 'decline') {
  return apiFetch<{ ok: true }>(
    `/v1/communities/${communityId}/requests/${userId}/${answer}`,
    postJson({}),
  );
}

export function createInvite(communityId: string, link: { days: number; maxUses: number }) {
  return apiFetch<InviteLink>(`/v1/communities/${communityId}/invites`, postJson(link));
}

export function turnOffInvite(communityId: string, inviteId: string) {
  return apiFetch<InviteLink>(
    `/v1/communities/${communityId}/invites/${inviteId}/turn-off`,
    postJson({}),
  );
}

export function setManager(communityId: string, userId: string, manager: boolean) {
  return apiFetch<{ role: MemberRole }>(
    `/v1/communities/${communityId}/members/${userId}/role`,
    postJson({ manager }),
  );
}

export function removeMember(communityId: string, userId: string) {
  return apiFetch<{ ok: true }>(
    `/v1/communities/${communityId}/members/${userId}/remove`,
    postJson({}),
  );
}

/** The communities the signed-in user is in: the library. */
export function fetchMyCommunities(): Promise<{ communities: LibraryCommunity[] }> {
  return apiFetch('/v1/me/communities');
}

export function search(q: string, signal?: AbortSignal): Promise<SearchResults> {
  return apiFetch(`/v1/search?q=${encodeURIComponent(q)}`, { signal });
}

export function leaveCommunity(communityId: string) {
  return apiFetch<{ ok: true }>(`/v1/communities/${communityId}/leave`, postJson({}));
}

export function fetchThread(postId: string): Promise<PostThread> {
  return apiFetch(`/v1/posts/${postId}`);
}

export function createComment(postId: string, body: string, parentId?: string) {
  return apiFetch<{ id: string }>(`/v1/posts/${postId}/comments`, postJson({ body, parentId }));
}

export function setGg(target: 'post' | 'comment', id: string, on: boolean) {
  return apiFetch<{ on: boolean; ggCount: number }>(`/v1/${target}s/${id}/gg`, postJson({ on }));
}

export function setPinned(postId: string, pinned: boolean) {
  return apiFetch<{ pinned: boolean }>(`/v1/posts/${postId}/pin`, postJson({ pinned }));
}

export function removePost(postId: string) {
  return apiFetch<{ ok: true }>(`/v1/posts/${postId}/remove`, postJson({}));
}

export function removeComment(commentId: string) {
  return apiFetch<{ ok: true }>(`/v1/comments/${commentId}/remove`, postJson({}));
}

export function fetchInvite(code: string): Promise<Invite> {
  return apiFetch(`/v1/invites/${encodeURIComponent(code)}`);
}

/** Asks to join; a manager accepts. */
export function joinWithInvite(code: string): Promise<JoinAnswer> {
  return apiFetch(`/v1/invites/${encodeURIComponent(code)}/join`, postJson({}));
}

export function cancelJoinRequest(code: string) {
  return apiFetch<{ ok: true }>(`/v1/invites/${encodeURIComponent(code)}/cancel`, postJson({}));
}

/** "5 minutes ago", "3 hours ago", "12 Oct". */
export function timeAgo(iso: string, now = Date.now()): string {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** The developer dashboard, where developers manage invites and builds. */
export const DEV_SITE: string = import.meta.env.PROD
  ? 'https://dev.mogzo.com'
  : 'http://localhost:5175';
